import axios from 'axios';

/**
 * Backend contract for vaccine record validation failures returned by the pet save
 * endpoint (`POST /pets` / `PATCH /pets/:id`).
 *
 * The response has the shape:
 * `{ code, message, reason, errors: { "pet_vaccine_expirations.file": [code] } }`
 *
 * Keeping the codes and the errors key here means a contract drift is a one line change.
 */
export const VACCINE_RECORD_ERROR_CODES = ['invalid_vaccine_record', 'unable_to_validate'] as const;

export const VACCINE_RECORD_ERRORS_KEY = 'pet_vaccine_expirations.file';

export type VaccineRecordErrorVariant = (typeof VACCINE_RECORD_ERROR_CODES)[number];

const isVaccineRecordErrorVariant = (value: unknown): value is VaccineRecordErrorVariant => {
    return (
        typeof value === 'string' &&
        VACCINE_RECORD_ERROR_CODES.includes(value as VaccineRecordErrorVariant)
    );
};

/**
 * Classifies a failed pet save as a vaccine record validation error.
 *
 * Detection is deliberately narrow: anything that is not one of the two known codes
 * returns `undefined` so the caller keeps its existing error handling.
 */
export const classifyVaccineRecordError = (
    error: unknown
): VaccineRecordErrorVariant | undefined => {
    if (!axios.isAxiosError(error)) {
        return undefined;
    }

    const data: unknown = error.response?.data;

    if (typeof data !== 'object' || data === null) {
        return undefined;
    }

    const { code, errors } = data as { code?: unknown; errors?: unknown };

    if (isVaccineRecordErrorVariant(code)) {
        return code;
    }

    if (typeof errors !== 'object' || errors === null) {
        return undefined;
    }

    const fileErrors: unknown = (errors as Record<string, unknown>)[VACCINE_RECORD_ERRORS_KEY];

    if (!Array.isArray(fileErrors)) {
        return undefined;
    }

    return fileErrors.find(isVaccineRecordErrorVariant);
};
